import React, { createContext, useContext, useEffect, useRef } from 'react';
import { WebSocketClient } from '@/shared/api/base';
import { WEBSOCKET_URL } from '@/shared/config';
import { useAppStore } from '@/shared/lib/store';
import { logger } from '@/shared/lib';
import type { WebSocketMessage } from '@/shared/lib/types';

interface WebSocketContextValue {
  send: (data: any) => void;
  isConnected: boolean;
}

const WebSocketContext = createContext<WebSocketContextValue>({
  send: () => {},
  isConnected: false,
});

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const wsRef = useRef<WebSocketClient | null>(null);
  const { setWsConnected, handleWebSocketMessage, wsConnected } = useAppStore();

  useEffect(() => {
    if (!wsRef.current) {
      wsRef.current = new WebSocketClient(WEBSOCKET_URL);
    }

    const handleMessage = (data: WebSocketMessage) => {
      handleWebSocketMessage(data);
    };

    const handleError = (error: Event) => {
      logger.error('WebSocket error:', error);
      setWsConnected(false);
    };

    wsRef.current.connect(handleMessage, handleError);

    // Set connection status
    setWsConnected(true);

    return () => {
      if (wsRef.current) {
        wsRef.current.disconnect();
        setWsConnected(false);
      }
    };
  }, [setWsConnected, handleWebSocketMessage]);

  const send = (data: any) => {
    if (wsRef.current) {
      wsRef.current.send(data);
    }
  };

  const value: WebSocketContextValue = {
    send,
    isConnected: wsConnected,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};